import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { T } from "./office";
import { useTeksTekstur, PapanTeks } from "./tekstur";

// Perabot & hiasan kantor 3D (dipakai OfficeRoom & Office3D): primitif balok/silinder, lukisan, jam, gitar, papan,
// rak buku + LED, kursi, pingpong, sofa, uap kopi, printer, dispenser, tanaman.
export const KAYU = "#d9b57a", KAYU_GELAP = "#b8935a", PUTIH = "#fbfaf6", LOGAM = "#8a909c", GELAP = "#2b2f3a";
export const B = ({ p, s, c, r, e, ei = 0, o = 1, ...rest }) => (
  <mesh position={p} rotation={r} castShadow receiveShadow {...rest}><boxGeometry args={s} /><meshStandardMaterial color={c} emissive={e || "#000"} emissiveIntensity={ei} transparent={o < 1} opacity={o} roughness={.85} /></mesh>
);
export const C = ({ p, a, c, r }) => <mesh position={p} rotation={r} castShadow><cylinderGeometry args={a} /><meshStandardMaterial color={c} roughness={.8} /></mesh>;

export function Lukisan({ p, r, warna = ["#2e58c4", "#f4be34", "#de3a30"], lebar = 1.4 }) {
  return (
    <group position={p} rotation={r}>
      <B p={[0, 0, 0]} s={[lebar, 1, 0.06]} c={KAYU_GELAP} /><B p={[0, 0, 0.035]} s={[lebar - .14, .86, 0.02]} c="#f7f3ea" />
      <B p={[-lebar * .18, -.1, 0.05]} s={[lebar * .35, .45, 0.01]} c={warna[0]} /><B p={[lebar * .2, .1, 0.05]} s={[lebar * .3, .3, 0.01]} c={warna[1]} />
      <mesh position={[lebar * .1, -.22, 0.055]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[.14, .14, .01, 16]} /><meshStandardMaterial color={warna[2]} /></mesh>
    </group>
  );
}

export function JamDinding({ p }) {
  const jam = useRef(), menit = useRef(), detik = useRef();
  useFrame(() => {
    const d = new Date(), sd = d.getSeconds() + d.getMilliseconds() / 1000, m = d.getMinutes() + sd / 60, h = (d.getHours() % 12) + m / 60;
    if (detik.current) detik.current.rotation.z = -sd / 60 * Math.PI * 2;
    if (menit.current) menit.current.rotation.z = -m / 60 * Math.PI * 2;
    if (jam.current) jam.current.rotation.z = -h / 12 * Math.PI * 2;
  });
  return (
    <group position={p}>
      <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[.42, .42, .06, 24]} /><meshStandardMaterial color={GELAP} /></mesh>
      <mesh position={[0, 0, .03]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[.36, .36, .02, 24]} /><meshStandardMaterial color="#ffffff" /></mesh>
      {[0, 1, 2, 3].map(i => <B key={i} p={[Math.sin(i * Math.PI / 2) * .28, Math.cos(i * Math.PI / 2) * .28, .05]} s={[.04, .08, .01]} c={GELAP} r={[0, 0, -i * Math.PI / 2]} />)}
      <group ref={jam} position={[0, 0, .06]}><B p={[0, .1, 0]} s={[.04, .2, .01]} c={GELAP} /></group>
      <group ref={menit} position={[0, 0, .07]}><B p={[0, .14, 0]} s={[.03, .3, .01]} c={GELAP} /></group>
      <group ref={detik} position={[0, 0, .08]}><B p={[0, .15, 0]} s={[.012, .32, .01]} c="#de3a30" /></group>
    </group>
  );
}

export function Gitar({ p }) {
  return (
    <group position={p} rotation={[0, Math.PI / 2, 0.15]}>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -.3, 0]}><cylinderGeometry args={[.34, .34, .09, 20]} /><meshStandardMaterial color="#c8863a" /></mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, .05, 0]}><cylinderGeometry args={[.26, .26, .09, 20]} /><meshStandardMaterial color="#c8863a" /></mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -.22, .05]}><cylinderGeometry args={[.1, .1, .02, 16]} /><meshStandardMaterial color="#3a2a1a" /></mesh>
      <B p={[0, .55, 0]} s={[.1, .9, .05]} c="#5a3a22" /><B p={[0, 1.05, 0]} s={[.16, .22, .05]} c="#2a1a10" />
      {[-.03, -.01, .01, .03].map(x => <B key={x} p={[x, .3, .04]} s={[.005, 1.4, .005]} c="#e8e8e8" />)}
    </group>
  );
}

export function PapanTulis({ p }) {
  return (
    <group position={p}>
      <B p={[0, 0, 0]} s={[3, 1.5, 0.08]} c={LOGAM} /><B p={[0, 0, 0.045]} s={[2.84, 1.34, 0.02]} c="#ffffff" />
      <B p={[-.7, .3, .06]} s={[1.1, .05, .01]} c={T.accent} /><B p={[-.5, .1, .06]} s={[1.5, .05, .01]} c={T.accent} /><B p={[.6, -.2, .06]} s={[.9, .5, .01]} c="#de3a30" o={.4} /><B p={[-.6, -.3, .06]} s={[1.2, .05, .01]} c="#42a060" />
      <B p={[0, -.82, .1]} s={[3, .06, .2]} c={LOGAM} />
    </group>
  );
}

// Papan logo menempel di dinding belakang (tekstur, bukan HTML) + lampu sorot kecil yang berdenyut
export function PapanLogo({ p }) {
  const tx = useTeksTekstur({ baris: ["JAWSEL OFFICE"], sub: "Multi-Agent Command Center", w: 1024, h: 320, bg: "#ffffff", warna: T.accent, ukuran: 110, radius: 60, garis: T.accent });
  const lampu = useRef();
  useFrame(({ clock }) => { if (lampu.current) lampu.current.intensity = 1.6 + Math.sin(clock.getElapsedTime() * 2) * .4; });
  return (
    <group position={p}>
      <B p={[0, 0, -0.03]} s={[3.6, 1.2, 0.08]} c="#e9e6dc" />
      <PapanTeks tekstur={tx} lebar={3.4} tinggi={1.06} position={[0, 0, 0.02]} />
      <B p={[0, 0.85, 0.3]} s={[2.6, 0.06, 0.5]} c={LOGAM} /><pointLight ref={lampu} position={[0, 0.7, 0.6]} color="#fff2d0" intensity={1.6} distance={4} />
    </group>
  );
}

export function RakBuku({ p }) {
  const buku = ["#2e58c4", "#de3a30", "#f4be34", "#42a060", "#8a56c8", "#ec802e", "#28304a", "#6ebe96"];
  return (
    <group position={p}>
      <B p={[0, 1.1, -0.17]} s={[1.5, 2.2, .06]} c={KAYU_GELAP} />{[-0.72, 0.72].map(x => <B key={x} p={[x, 1.1, 0]} s={[.06, 2.2, .4]} c={KAYU} />)}<B p={[0, 2.18, 0]} s={[1.5, .05, .4]} c={KAYU} />
      {[0.1, 0.8, 1.5].map((y, j) => <group key={y}><B p={[0, y, 0]} s={[1.4, .05, .38]} c={KAYU} />{[0, 1, 2, 3, 4, 5].map(i => <B key={i} p={[-.55 + i * .22, y + .25 + (i % 3) * .03, 0]} s={[.17, .44 + (i % 3) * .06, .28]} c={buku[(i + j * 2) % buku.length]} />)}</group>)}
    </group>
  );
}

// LED server berkelip acak (hijau/biru/oranye sesekali)
export function LedRak({ n = 8, pos }) {
  const refs = useRef([]), fase = useMemo(() => Array.from({ length: n }, () => Math.random() * 10), [n]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    refs.current.forEach((m, i) => { if (m) { const on = Math.sin(t * (3 + (i % 4)) + fase[i]) > (i % 3 ? 0.2 : 0.7); m.material.emissiveIntensity = on ? 1.4 : 0.08; } });
  });
  return (
    <group position={pos}>
      {fase.map((_, i) => <mesh key={i} ref={el => (refs.current[i] = el)} position={[-.32 + (i % 4) * .21, .35 + Math.floor(i / 4) * .85, .41]}><boxGeometry args={[.1, .05, .01]} /><meshStandardMaterial color={i % 5 === 0 ? "#f4be34" : i % 2 ? "#3ad66a" : "#4a7dff"} emissive={i % 5 === 0 ? "#f4be34" : i % 2 ? "#3ad66a" : "#4a7dff"} emissiveIntensity={1} /></mesh>)}
    </group>
  );
}

export function Kursi({ p, r, c = T.accent }) {
  return (
    <group position={p} rotation={r}>
      <B p={[0, 0.34, 0]} s={[0.5, 0.08, 0.5]} c={c} /><B p={[0, 0.66, 0.23]} s={[0.5, 0.56, 0.08]} c={c} />
      <C p={[0, 0.17, 0]} a={[0.04, 0.04, 0.3, 8]} c={LOGAM} />{[0, 1, 2, 3, 4].map(i => <B key={i} p={[Math.sin(i * 1.257) * .18, .03, Math.cos(i * 1.257) * .18]} s={[.08, .04, .3]} c={LOGAM} r={[0, i * 1.257, 0]} />)}
    </group>
  );
}

export function Pingpong({ p }) {
  return (
    <group position={p}>
      <B p={[0, .76, 0]} s={[2.8, .06, 1.55]} c="#2e9e5b" /><B p={[0, .765, 0]} s={[2.8, .062, .04]} c="#fff" /><B p={[0, .765, 0]} s={[.04, .062, 1.55]} c="#fff" />
      <B p={[0, .88, 0]} s={[.03, .18, 1.75]} c="#333" o={.6} />
      {[[-1.1, -.55], [1.1, -.55], [-1.1, .55], [1.1, .55]].map(([x, z]) => <B key={x + z} p={[x, .36, z]} s={[.08, .72, .08]} c={LOGAM} />)}
      <B p={[-.8, .8, .3]} s={[.3, .02, .32]} c="#de3a30" /><C p={[0.7, .82, -.2]} a={[.05, .05, .05, 10]} c="#fff" />
    </group>
  );
}

export function Sofa({ p, r, c = "#3b5bc4", lebar = 2.4 }) {
  const terang = useMemo(() => "#" + new THREE.Color(c).lerp(new THREE.Color("#ffffff"), .25).getHexString(), [c]);
  return (
    <group position={p} rotation={r}>
      <B p={[0, .22, 0]} s={[lebar, .44, .9]} c={c} /><B p={[0, .6, -.35]} s={[lebar, .5, .22]} c={c} />
      <B p={[-lebar / 2 + .12, .45, 0]} s={[.24, .3, .9]} c={c} /><B p={[lebar / 2 - .12, .45, 0]} s={[.24, .3, .9]} c={c} />
      <B p={[0, .46, .05]} s={[lebar - .5, .06, .8]} c={terang} /><B p={[-lebar / 4, .62, -.2]} s={[.4, .3, .14]} c="#f4be34" r={[0, 0, .2]} />
    </group>
  );
}

export function Uap({ p }) {
  const refs = useRef([]);
  useFrame(({ clock }) => { refs.current.forEach((m, i) => { if (m) { const t = (clock.getElapsedTime() * .5 + i / 3) % 1; m.position.set(Math.sin(t * 6 + i) * .05, t * .45, 0); m.material.opacity = .45 * (1 - t); m.scale.setScalar(.6 + t); } }); });
  return <group position={p}>{[0, 1, 2].map(i => <mesh key={i} ref={el => (refs.current[i] = el)}><sphereGeometry args={[.045, 8, 8]} /><meshBasicMaterial color="#ffffff" transparent opacity={.4} /></mesh>)}</group>;
}

export function Printer({ p }) {
  const led = useRef();
  useFrame(({ clock }) => { if (led.current) led.current.material.emissiveIntensity = Math.sin(clock.getElapsedTime() * 2.5) > 0 ? 1.2 : .15; });
  return <group position={p}><B p={[0, .4, 0]} s={[.9, .8, .7]} c={PUTIH} /><B p={[0, .95, 0]} s={[.8, .3, .6]} c="#e3e5ea" /><B p={[0, 1.12, .1]} s={[.5, .04, .3]} c="#fff" /><mesh ref={led} position={[.25, 1.0, .31]}><boxGeometry args={[.2, .06, .01]} /><meshStandardMaterial color="#3ad66a" emissive="#3ad66a" emissiveIntensity={1} /></mesh></group>;
}

export function Dispenser({ p }) {
  return <group position={p}><B p={[0, .55, 0]} s={[.42, 1.1, .42]} c={PUTIH} /><C p={[0, 1.4, 0]} a={[.17, .13, .5, 14]} c="#9ed4f2" /><B p={[0, .85, .22]} s={[.1, .1, .02]} c="#2e58c4" /><B p={[.12, .85, .22]} s={[.1, .1, .02]} c="#de3a30" /></group>;
}

export function Tanaman({ pos, tinggi = 1 }) {
  return (
    <group position={pos} scale={[tinggi, tinggi, tinggi]}>
      <C p={[0, 0.22, 0]} a={[0.22, 0.17, 0.44, 12]} c="#e7d5b0" />
      {[[0, 0.75, 0, .36], [-0.18, 0.62, 0.1, .24], [0.2, 0.66, -0.08, .26]].map(([x, y, z, r], i) => <mesh key={i} position={[x, y, z]} castShadow><sphereGeometry args={[r, 10, 10]} /><meshStandardMaterial color={i ? "#5fae4e" : "#4f9a4a"} /></mesh>)}
    </group>
  );
}

// Papan divisi: totem berdiri dengan papan bertekstur warna divisi, menghadap kamera
